import React, { useMemo, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Container,
  VStack,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../api/auth";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const tokenError = !token ? "Missing reset token." : "";

  const passwordError = !newPassword
    ? "New password is required."
    : newPassword.length < 8
      ? "Password must be at least 8 characters."
      : "";

  const confirmPasswordError = !confirmNewPassword
    ? "Please confirm your new password."
    : newPassword !== confirmNewPassword
      ? "Passwords do not match."
      : "";

  async function onSubmit(e) {
    e.preventDefault();
    setApiError("");
    setSuccessMessage("");

    if (tokenError || passwordError || confirmPasswordError) return;

    try {
      setSubmitting(true);

      await resetPassword({
        token,
        new_password: newPassword,
      });

      setSuccessMessage("Password updated successfully. Redirecting to login...");

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Unable to reset password.";

      setApiError(typeof msg === "string" ? msg : "Unable to reset password.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <style>{`
        .poolBackground {
          background: linear-gradient(120deg,#0a3d62,#1e81b0,#3caea3,#1e81b0,#0a3d62);
          background-size: 400% 400%;
          animation: poolShimmer 8s ease infinite;
        }
        @keyframes poolShimmer {
          0% { background-position: 0% 50%; filter: brightness(100%); }
          50% { background-position: 100% 50%; filter: brightness(120%); }
          100% { background-position: 0% 50%; filter: brightness(100%); }
        }
      `}</style>

      <Box minH="100vh" className="poolBackground" color="white">
        <Box bg="rgba(0, 0, 40, 0.4)" minH="100vh" px={4} py={20}>
          <Container maxW="container.md" centerContent>
            <VStack spacing={8} align="stretch">
              <Box bg="#00007f" px={8} py={6} borderRadius="md" boxShadow="lg" textAlign="center">
                <Heading size={{ base: "xl", md: "2xl" }}>Choose New Password</Heading>
              </Box>

              <Box
                as="form"
                onSubmit={onSubmit}
                bg="rgba(255, 255, 255, 0.15)"
                px={6}
                py={8}
                borderRadius="md"
                boxShadow="xl"
                backdropFilter="blur(6px)"
              >
                <VStack spacing={4}>
                  {tokenError ? (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {tokenError}
                    </Alert>
                  ) : null}

                  {apiError ? (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {apiError}
                    </Alert>
                  ) : null}

                  {successMessage ? (
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      {successMessage}
                    </Alert>
                  ) : null}

                  <FormControl isInvalid={!!passwordError && newPassword.length > 0}>
                    <FormLabel>New Password</FormLabel>
                    <Input
                      placeholder="New Password"
                      type="password"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <FormErrorMessage>{passwordError}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!confirmPasswordError && confirmNewPassword.length > 0}
                  >
                    <FormLabel>Confirm New Password</FormLabel>
                    <Input
                      placeholder="Confirm New Password"
                      type="password"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    bg="#00007f"
                    color="white"
                    w="100%"
                    isLoading={submitting}
                    loadingText="Updating password"
                    isDisabled={!token}
                  >
                    Save New Password
                  </Button>

                  <Text fontSize="sm">
                    <Link as={RouterLink} to="/login" color="orange.200" fontWeight="600">
                      Back to Login
                    </Link>
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
}